import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { loadStakedNodes, scanLiquidatable } from '../../context/useCynths';
import { shortAddress } from '../../utils';
import { describePosition, num, formatUsd, formatAmount, formatPct, formatRatio } from './cdpHelpers';

const GOLDEN_MAX_ID = 10;

/** Positions below the liquidation ratio; only owners of staked NFT nodes can liquidate them */
const Liquidations = ({ market, account, address, actions, refreshKey, onRefresh }) => {
  const t = useT();
  const [nodes, setNodes] = useState(null);
  const [nodeId, setNodeId] = useState(null);
  const [positions, setPositions] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [staked, liquidatable] = await Promise.all([address ? loadStakedNodes(address) : [], scanLiquidatable()]);
      setNodes(staked);
      setNodeId((current) => (staked.includes(current) ? current : staked[0] ?? null));
      setPositions(liquidatable);
    } catch (err) {
      console.error('Liquidation scan failed:', err);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const golden = nodeId !== null && nodeId <= GOLDEN_MAX_ID;
  // repaying with its own synths the node takes most of the penalty; through a pool the depositors keep most of it
  const shareBps = golden ? market.goldenShareBps : market.nodeShareBps;
  const poolShareBps = golden ? market.goldenPoolShareBps : market.poolShareBps;

  return (
    <div className="space-y-4">
      <p className="text-sm text-white/60">{t('cynths.liqBlurb')}</p>

      <div className="lb-tile flex flex-wrap items-center justify-between gap-3 p-4">
        {!address ? (
          <p className="text-sm text-amber-300">{t('cynths.connectFirst')}</p>
        ) : nodes && nodes.length === 0 ? (
          <p className="text-sm text-amber-300">
            {t('cynths.noStakedNodes')}{' '}
            <Link to="/nftnodes" className="lb-link">
              {t('cynths.goToNodes')}
            </Link>
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="lb-label">{t('cynths.liquidateAs')}</span>
            <select
              value={nodeId ?? ''}
              onChange={(e) => setNodeId(Number(e.target.value))}
              className="lb-input w-auto py-2 pl-3 pr-8"
            >
              {(nodes || []).map((id) => (
                <option key={id} value={id}>
                  Node #{id}
                  {id <= GOLDEN_MAX_ID ? ' ★' : ''}
                </option>
              ))}
            </select>
            {nodeId !== null && (
              <span className="text-xs text-white/55">
                {t('cynths.nodeShare', { pool: formatPct(poolShareBps), own: formatPct(shareBps) })}
                {golden ? ` (${t('cynths.golden')})` : ''}
              </span>
            )}
          </div>
        )}
        <button className="lb-btn lb-btn-outline lb-btn-sm" onClick={() => Promise.all([load(), onRefresh()])} disabled={loading}>
          {loading ? t('common.loading') : t('cynths.rescan')}
        </button>
      </div>

      {positions && positions.length === 0 && <p className="text-sm text-white/55">{t('cynths.noLiquidatable')}</p>}

      <div className="space-y-3">
        {(positions || []).map((p) => {
          const v = describePosition(p, market);
          const poolSynths = v.synth.poolStats ? num(v.synth.poolStats.synthBalance) : 0;
          const walletSynth = account ? num(account.balances[v.synth.token.toLowerCase()]) : 0;
          const canAct = nodeId !== null;
          const ownRepay = Math.min(walletSynth, v.debt);
          return (
            <div key={p.id} className="lb-tile flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div className="grid flex-1 grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <div>
                  <p className="lb-label">#{p.id}</p>
                  <p className="notranslate text-white">{shortAddress(p.owner)}</p>
                </div>
                <div>
                  <p className="lb-label">{t('cynths.debt')}</p>
                  <p className="notranslate lb-num text-white">
                    {formatAmount(v.debt, 6)} {v.synth.ticker}
                  </p>
                  <p className="notranslate text-xs text-white/45">{formatUsd(v.debtUsd)}</p>
                </div>
                <div>
                  <p className="lb-label">{t('cynths.collateral')}</p>
                  <p className="notranslate lb-num text-white">
                    {formatAmount(v.collateralAmount)} {v.collateral.symbol}
                  </p>
                  <p className="notranslate text-xs text-white/45">{formatUsd(v.collateralUsd)}</p>
                </div>
                <div>
                  <p className="lb-label">{t('cynths.collateralRatio')}</p>
                  <p className="notranslate lb-num text-rose-400">{v.ratio === null ? 'N/A' : formatRatio(v.ratio)}</p>
                  <p className="text-xs text-white/45">
                    {t('cynths.liqShort')} {formatPct(v.collateral.liquidationRatioBps)}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 md:w-56">
                <button
                  className="lb-btn lb-btn-primary lb-btn-sm"
                  disabled={!canAct || poolSynths === 0}
                  title={poolSynths === 0 ? t('cynths.poolEmpty') : undefined}
                  onClick={() => actions.liquidateWithPool(p.id, nodeId)}
                >
                  {t('cynths.liquidateWithPool')}
                </button>
                <button
                  className="lb-btn lb-btn-outline lb-btn-sm"
                  disabled={!canAct || ownRepay === 0}
                  onClick={() =>
                    actions.liquidate(p.id, nodeId, v.synth.token, ownRepay >= v.debt ? p.debt : account.balances[v.synth.token.toLowerCase()])
                  }
                >
                  {t('cynths.liquidateWithOwn', { amount: formatAmount(ownRepay, 4), ticker: v.synth.ticker })}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Liquidations;
